import React from "react";
import { Button, IconButton, CircularProgress } from "@mui/material";
import { SxProps, Theme } from "@mui/system";
import { ButtonType, ButtonColor } from "../constants/enums";

// Import icons
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";


interface MyButtonProps 
 {
  onClick: () => void;
  actionType?: ButtonType; // Optional predefined type
  variant?: "text" | "contained" | "outlined";
  color?: ButtonColor; // Ensure the correct enum type is used
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  type?: "button" | "submit" | "reset";
}

const getButtonConfig = (actionType: ButtonType) => {
  switch (actionType) {
    case ButtonType.EDIT:
      return { icon: <EditIcon />, label: "Éditer", color: ButtonColor.PRIMARY };
    case ButtonType.INSERT:
      return { icon: <AddIcon />, label: "Ajouter", color: ButtonColor.SUCCESS };
    case ButtonType.DELETE:
      return { icon: <DeleteIcon />, label: "Supprimer", color: ButtonColor.ERROR };
    case ButtonType.PRINT:
      return { icon: <PrintIcon />, label: "Imprimer", color: ButtonColor.SECONDARY };
    case ButtonType.DOWNLOAD:
      return { icon: <DownloadIcon />, label: "Télécharger", color: ButtonColor.INFO };
    case ButtonType.CANCEL:
      return { icon: <CancelIcon />, label: "Annuler", color: ButtonColor.INFO };
    case ButtonType.VIEW:
      return { icon: <VisibilityIcon />, label: "Détails", color: ButtonColor.SECONDARY }; // ou ButtonColor.WARNING
      default:
      return { icon: null, label: null, color: undefined };
  }
};

const MyButton: React.FC<MyButtonProps> = ({
  onClick,
  actionType = ButtonType.DEFAULT,
  variant = "contained",
  color, // Reintroduced custom color prop
  size = "medium",
  disabled = false,
  loading = false,
  type = "button",
  sx,
  children,
}) => {
  const { icon, color: predefinedColor } = getButtonConfig(actionType);
  const hasPredefinedType = actionType !== ButtonType.DEFAULT;

  // Determine color to use: prefer provided color, fallback to predefined or default color
  const finalColor = color ?? predefinedColor ?? ButtonColor.PRIMARY;

  return hasPredefinedType ? (
    // If type is defined, use IconButton
    <IconButton
      onClick={onClick}
      color={finalColor} // Use provided color or predefined one
      disabled={disabled || loading}
      sx={sx}
    >
      {loading ? <CircularProgress size={24} /> : icon}
    </IconButton>
  ) : (
    // Otherwise, use a normal Button
    <Button
      onClick={onClick}
      variant={variant}
      color={finalColor} // Use provided color or predefined one
      size={size}
      disabled={disabled || loading}
      type={type}
      sx={sx}
    
    >
      {loading ? <CircularProgress size={24} color="inherit" /> : children}
    </Button>
  );
};

export default MyButton;
